import React from 'react';

interface ElegantChallengeBannerProps {
  challengeType: 'daily' | 'weekly';
  challengeTitle: string;
  challengePrompt: string;
  currentWordCount: number;
  targetWordCount: number;
  xpReward: number;
  isMobile?: boolean;
}

const ElegantChallengeBanner: React.FC<ElegantChallengeBannerProps> = ({
  challengeType,
  challengeTitle,
  challengePrompt,
  currentWordCount,
  targetWordCount,
  xpReward,
  isMobile = false
}) => {
  const percentage = Math.min(100, (currentWordCount / targetWordCount) * 100);
  const isDaily = challengeType === 'daily';
  const isComplete = currentWordCount >= targetWordCount;
  
  const theme = {
    color: isDaily ? '#F59E0B' : '#6C5CE7',
    lightBg: isDaily ? '#FFFBEB' : '#F5F3FF',
    icon: isDaily ? '⏰' : '📚'
  };

  return (
    <div style={{
      background: theme.lightBg,
      borderBottom: `2px solid ${theme.color}`,
      padding: isMobile ? '12px 16px' : '16px 32px',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '12px' : '20px',
      transition: 'all 0.3s ease'
    }}>
      {/* Icon */}
      <div style={{
        fontSize: isMobile ? '1.5rem' : '2rem',
        flexShrink: 0
      }}>
        {isComplete ? '✅' : theme.icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Title & XP */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '6px',
          gap: '12px'
        }}>
          <div style={{
            fontSize: isMobile ? '0.9rem' : '1rem',
            fontWeight: '700',
            color: theme.color,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {challengeType === 'daily' ? 'Today\'s Challenge' : 'This Week\'s Challenge'}
          </div>
          <div style={{
            background: theme.color,
            color: 'white',
            padding: isMobile ? '3px 10px' : '5px 14px',
            borderRadius: '12px',
            fontSize: isMobile ? '0.8rem' : '0.85rem',
            fontWeight: '700',
            whiteSpace: 'nowrap'
          }}>
            +{xpReward} XP
          </div>
        </div>

        {/* Prompt - Desktop only or when expanded */}
        {!isMobile && (
          <div style={{
            fontSize: '1rem',
            color: '#666',
            fontStyle: 'italic',
            marginBottom: '8px',
            lineHeight: '1.5',
            fontWeight: '500'
          }}>
            "{challengePrompt}"
          </div>
        )}

        {/* Progress */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Progress Bar */}
          <div style={{
            flex: 1,
            height: isMobile ? '6px' : '8px',
            background: 'rgba(0,0,0,0.1)',
            borderRadius: '100px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${percentage}%`,
              height: '100%',
              background: isComplete 
                ? 'linear-gradient(90deg, #10B981, #059669)'
                : `linear-gradient(90deg, ${theme.color}, ${isDaily ? '#D97706' : '#5B4BC4'})`,
              borderRadius: '100px',
              transition: 'width 0.5s ease'
            }} />
          </div>

          {/* Word Count */}
          <div style={{
            fontSize: isMobile ? '0.85rem' : '0.95rem',
            fontWeight: '700',
            color: isComplete ? '#10B981' : theme.color,
            whiteSpace: 'nowrap'
          }}>
            {currentWordCount.toLocaleString()} / {targetWordCount.toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElegantChallengeBanner;